import {useEffect, useRef} from "preact/hooks";
import {PlayIcon} from "@heroicons/react/24/solid";

export function Song({song, small, compact, onSongClick, className = ''}) {

    const {
        category_name,
        category_id,
        author_name,
        title,
        youtube_url,
        date_added
    } = song;

    const imgRef = useRef(null);

    // Never emit a crawlable href with a blank "p=" when category_id is unresolved.
    const songHref = category_id ? `/song?s=${youtube_url}&p=${category_id}` : `/song?s=${youtube_url}`;

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const element = entry.target;
                    element.style.backgroundImage = `url(${element.dataset.src})`;
                    observer.unobserve(element);
                }
            });
        });

        if (imgRef.current) {
            imgRef.current.style.backgroundImage = "";
            observer.observe(imgRef.current);
        }

        return () => {
            if (imgRef.current) {
                observer.unobserve(imgRef.current);
            }
        };
    }, [youtube_url]);

    // Trim down date to only show the date
    let date_added2 = '';
    if (date_added) {
        const date = new Date(date_added);
        const options = {year: 'numeric', month: 'long', day: 'numeric'};
        date_added2 = date.toLocaleDateString('en-US', options);
    }

    return (
        <div className={`group relative overflow-hidden rounded-lg border border-border/60 bg-zinc-800 cursor-pointer ${className}`}>
            <div className={`absolute z-1 top-4 left-4 flex space-x-2 text-xs`}>
                {song.new === 'true' && (
                    <span
                        className="text-gray-300 text-xs bg-black/40 px-2 py-0.5 rounded-lg">New</span>
                )}
                {category_name && (
                    <a href={songHref}
                       className="text-gray-300 text-xs bg-black/40 px-2 py-0.5 rounded-lg">{category_name}</a>
                )}
            </div>

            <a
                href={songHref}
                onClick={e => {
                    if (typeof onSongClick === 'function') {
                        e.preventDefault();
                        onSongClick();
                    }
                }}
            >
                <div ref={imgRef}
                     className={`w-full ${compact ? 'h-45 md:h-24 bg-cover' : small ? 'h-40 bg-cover' : 'h-60 bg-auto'} transition-transform duration-500 group-hover:scale-110 bg-center`}
                     data-src={`https://i1.ytimg.com/vi/${youtube_url}/sddefault.jpg`}
                     role="img"
                     aria-label={`Thumbnail for ${title}`}
                     key={youtube_url}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 to-transparent" aria-hidden="true"></div>
                <div className="absolute inset-0 flex items-center justify-center duration-300 group-hover:opacity-100 opacity-0" aria-hidden="true">
                    <PlayIcon className="h-10 w-10 text-white"/>
                </div>
                <div className={`text-white absolute w-full bottom-0 text-left ${compact ? 'p-2' : 'p-4'}`}>
                    <div
                        className={`line-clamp-1 font-semibold ${compact ? 'text-sm leading-4 mb-0.5' : 'leading-5 mb-1'}`}>{title}</div>
                    <div className="flex justify-between align-items-center">
                        {author_name && (
                            <div
                                className={`whitespace-nowrap overflow-hidden block text-ellipsis max-w-[55%] text-gray-400 ${compact ? 'text-xs' : 'text-sm'}`}>{author_name}</div>
                        )}
                        {date_added2 && (
                            <div
                                className={`text-gray-400 bg-black/40 rounded-lg ${compact ? 'text-[10px] px-1 py-0.5' : 'text-xs px-1.5 py-0.5'}`}>{date_added2}</div>
                        )}
                    </div>
                </div>
            </a>
        </div>
    );
}
